import { AS400_EXPORT_WORKSHEET_COLUMNS } from '@erp/rpc';
import type { As400ExportGroup } from '@erp/rpc';
import type {
  AdditionalDescriptionLine,
  CellRule,
} from '../as400CellRuleKinds';

/**
 * GEN-2766: a Supplier 1 part number longer than its 25 characters is not cut
 * down. It moves: Supplier 1 Product # stays blank, the English description
 * (ITEM `IEDESC`) carries the part number, and past the description's 40
 * characters it continues on the Item Add lines, 40 characters each, from line
 * 10 down (10, 9, 8…). Supplier 2 and 3 keep the plain character limit.
 *
 * The description is on another worksheet, which the rule engine doesn't
 * reach, so the rule can't move the value itself. It explains the move instead
 * and spells out what goes where, ready to copy.
 */

const SUPPLIER_1_PART = 'ISPRD1';

/** A worksheet column's character limit, as the contract declares it. */
function columnLimit(group: As400ExportGroup, code: string): number {
  const column = AS400_EXPORT_WORKSHEET_COLUMNS[group].find(
    (candidate) => candidate.code === code,
  );
  if (column?.maxLength === undefined) {
    throw new Error(`${group} ${code} declares no character limit`);
  }
  return column.maxLength;
}

/** The marker that clears a field on a change row, as the contract declares it. */
function blankMarker(group: As400ExportGroup, code: string): string {
  const marker = AS400_EXPORT_WORKSHEET_COLUMNS[group].find(
    (candidate) => candidate.code === code,
  )?.blankMarker;
  if (marker === undefined) {
    throw new Error(`${group} ${code} declares no blank-out marker`);
  }
  return marker;
}

const PART_LIMIT = columnLimit('PURCHASE_DATA', SUPPLIER_1_PART);
const PART_BLANK_MARKER = blankMarker('PURCHASE_DATA', SUPPLIER_1_PART);
const DESCRIPTION_LIMIT = columnLimit('ITEM', 'IEDESC');
const ADDITIONAL_LINE_LIMIT = columnLimit('ITEM_ADD', 'IDDESC');
/** The Item Add line an overflow starts on; it works down to line 1. */
const FIRST_ADDITIONAL_LINE = 10;
/** Item Add's language (IDLANG) for English, the description it continues. */
const ENGLISH = 'E';

/** Where a long part number goes: the description, then Item Add lines. */
export interface DescriptionSplit {
  description: string;
  additionalLines: AdditionalDescriptionLine[];
  /** Characters that fit nowhere once line 1 is full. */
  leftOver: number;
}

/**
 * Split a value across the English description and the Item Add lines, from
 * line 10 down, each at its column's character limit.
 */
export function splitIntoDescription(value: string): DescriptionSplit {
  const description = value.slice(0, DESCRIPTION_LIMIT);
  const additionalLines: AdditionalDescriptionLine[] = [];
  let rest = value.slice(DESCRIPTION_LIMIT);
  for (let line = FIRST_ADDITIONAL_LINE; line >= 1 && rest; line--) {
    additionalLines.push({ line, text: rest.slice(0, ADDITIONAL_LINE_LIMIT) });
    rest = rest.slice(ADDITIONAL_LINE_LIMIT);
  }
  return { description, additionalLines, leftOver: rest.length };
}

/**
 * Supplier 1 Product # is at most 25 characters; a longer one belongs in the
 * description, and the violation says what to put where. Stands in for the
 * column's own limit check, whose advice (shorten it) would be wrong here.
 */
export const supplierPartOverflowRule: CellRule = {
  field: SUPPLIER_1_PART,
  replacesColumnLimit: true,
  check(value, row) {
    if (value === null || value.length <= PART_LIMIT) return null;
    return {
      rule: 'move-to-description',
      limit: PART_LIMIT,
      length: value.length,
      // On a change row a blank cell means "don't change", which would keep
      // the AS/400's old part number: the cell must be cleared out instead.
      clearWith: row.mode === 'change' ? PART_BLANK_MARKER : null,
      ...splitIntoDescription(value),
      lineLimit: ADDITIONAL_LINE_LIMIT,
      language: ENGLISH,
    };
  },
};
